import React from 'react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

/**
 * HeroSiteLogo — small, consistent site-branding strip placed at the top of
 * every game hero. Sources `siteDetails.siteLogo` from useSiteConfig().
 *
 * Renders nothing if the logo is missing — no broken <img>, no extra spacing.
 *
 * Props:
 *   className     — override outer wrapper (defaults to centered strip with top padding)
 *   imgClassName  — override the <img> classes
 *   tone          — 'light' | 'dark' (default 'dark') adjusts drop-shadow strength
 */
export default function HeroSiteLogo({ className, imgClassName, tone = 'dark' }) {
  // Disabled 2026-04: site logo is rendered inside each hero's primary logo
  // surface (charity plaque, hero card, etc.). The standalone strip above
  // those surfaces was a duplicate. Kept as a no-op so all call sites
  // remain valid and can be re-enabled later if needed.
  return null;

  // eslint-disable-next-line no-unreachable
  const { siteDetails } = useSiteConfig() || {};
  const logoUrl = siteDetails?.siteLogo;
  const siteName = siteDetails?.siteName || 'Site logo';

  if (!logoUrl) return null;

  const shadow =
    tone === 'light'
      ? 'drop-shadow-[0_2px_6px_rgba(0,0,0,0.15)]'
      : 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]';

  return (
    <div
      className={
        className ||
        'relative z-20 w-full flex justify-center pt-4 sm:pt-6 pb-2'
      }
    >
      <img
        src={logoUrl}
        alt={siteName}
        className={
          imgClassName ||
          `max-h-[64px] sm:max-h-[80px] md:max-h-[96px] max-w-[280px] w-auto object-contain ${shadow}`
        }
      />
    </div>
  );
}
